import clsx from "clsx";
import styles from "./styles.module.scss";
import Header from "../../../components/layout/header";
import Question from "../../../components/layout/question";
import Main from "../../../components/layout/main";
import React from "react";
import { useChatLog } from "../../../contexts/chat-log";
import { useChatbotConfig } from "../../../contexts/chatbot-config";
import MessageAi from "../../../components/ui/message-ai";
import MessageUser from "../../../components/ui/message-user";
import MessageLoading from "../../../components/ui/message-loading";
import BottomSheet from "../../../components/layout/bottom-sheet";
import Aside from "../../../components/layout/aside";
import { sendMessage, createAiMessageId } from "../../../libs/fns";

export default function Chat({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { chatLogs, setChatLogs, resetIdleTimer } = useChatLog();
  const { chatbotData } = useChatbotConfig();

  const mainRef = React.useRef<HTMLDivElement>(null);
  const chatRef = React.useRef<HTMLDivElement>(null);

  const bgImageUrl = chatbotData?.chatbot_background_image ?? null;

  // ヘッダーからチャットエリアまで背景画像を継ぎ目なく表示するため、
  // コンテナ全体（Header + Main）の背景として適用する
  const chatBgStyle = React.useMemo((): React.CSSProperties => {
    if (!bgImageUrl) return {};
    return {
      backgroundImage: `url(${bgImageUrl})`,
      backgroundRepeat: 'repeat-y',
      backgroundSize: '100% auto',
    };
  }, [bgImageUrl]);

  // ユーザーメッセージを画面上部にスクロールする関数
  const scrollToLastUserMessage = React.useCallback(() => {
    const performScroll = () => {
      const scroller = mainRef.current;
      if (!scroller) {
        return;
      }

      // 最大スクロール位置を計算
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;

      // 最下部までスクロール
      if (maxScroll > 0) {
        scroller.scrollTop = maxScroll;
      }
    };

    // DOM更新を待ってすぐにスクロール（一度だけ）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        performScroll();
      });
    });
  }, []);

  // モバイルソフトキーボード開閉時の白画面フラッシュ対策
  // キーボード表示でビューポート(dvh)が縮む際、Main領域のスクロール位置が
  // 追従せず一瞬空白（ホストページの白背景）が見えてしまうため、
  // visualViewport のリサイズに合わせて最下部へ再スクロールする
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;

    const handleViewportResize = () => {
      const scroller = mainRef.current;
      if (!scroller) return;
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      if (maxScroll > 0) {
        scroller.scrollTop = maxScroll;
      }
    };

    const vv = window.visualViewport;
    vv.addEventListener("resize", handleViewportResize);
    return () => {
      vv.removeEventListener("resize", handleViewportResize);
    };
  }, []);

  async function handleAnswerClick(answer: string, messageIndex: number) {
    resetIdleTimer();
    // このクリックで生成するAIメッセージの一意ID。
    // 複数の提案回答を連続でクリックして複数のストリーミングが並行しても、
    // 配列上の位置（インデックス）ではなくIDで対象を特定することで、
    // 他のメッセージの内容を誤って上書きしないようにする。
    const thisAiMessageId = createAiMessageId();

    setChatLogs((prev) => {
      // 1. answers_usedフラグを立てる
      const updatedLogs = prev.map((log, index) => {
        if (index === messageIndex) {
          return {
            ...log,
            message: {
              ...log.message,
              answers_used: true,
            },
          };
        }
        return log;
      });

      // 2. ユーザーメッセージとローディングを追加
      const logsWithUserAndLoading = [
        ...updatedLogs,
        {
          type: "USER" as const,
          message: {
            model: answer,
            answer: [],
            url: [],
            asset_data: [],
          },
        },
        {
          type: "LOADING" as const,
          message: {
            model: "",
            answer: [],
            url: [],
            asset_data: [],
          },
        },
      ];

      return logsWithUserAndLoading;
    });

    // ユーザーメッセージ追加直後にスクロール
    scrollToLastUserMessage();

    // ストリーミング開始前にLOADINGをAIメッセージに変換
    setChatLogs((prev) => [
      ...prev.filter((chatLog) => chatLog.type !== "LOADING"),
      {
        type: "AI" as const,
        id: thisAiMessageId,
        message: {
          model: "",
          answer: [],
          url: [],
          asset_data: [],
          streaming_completed: false,
        },
      },
    ]);

    // APIを呼び出し（ストリーミング対応）
    const newMessage = await sendMessage(
      answer,
      (accumulatedText: string) => {
        // ストリーミング中のテキスト更新（このクリックで発行したIDのメッセージのみ更新）
        setChatLogs((prev) => {
          const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);

          if (targetIndex === -1) return prev;

          return prev.map((log, index) => {
            if (index === targetIndex) {
              return {
                ...log,
                message: {
                  ...log.message,
                  model: accumulatedText, // 累積テキスト全体を保存
                  streaming_completed: false, // まだストリーミング中
                },
              };
            }
            return log;
          });
        });
      },
      (meta) => {
        // メタデータ（ai_question/answer/campaign_text/cta_data/display_link/asset_data）が
        // 解析され次第、本文と同時進行でAIメッセージへ即時反映する（一括表示による表示遅延を防ぐ）
        setChatLogs((prev) => {
          const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);

          if (targetIndex === -1) return prev;

          return prev.map((log, index) => {
            if (index === targetIndex) {
              return {
                ...log,
                message: {
                  ...log.message,
                  ai_question: meta.ai_question || log.message.ai_question,
                  answer: meta.answer.length ? meta.answer : log.message.answer,
                  campaign_text: meta.campaign_text || log.message.campaign_text,
                  cta_data: meta.cta_data.length ? meta.cta_data : log.message.cta_data,
                  display_link: meta.display_link.length ? meta.display_link : log.message.display_link,
                  asset_data: meta.asset_data.length ? meta.asset_data : log.message.asset_data,
                  asset_description: meta.asset_description || log.message.asset_description,
                },
              };
            }
            return log;
          });
        });
      },
      (resetMessage?: string) => {
        // onReset: セッション初期化時にチャットログをクリア
        // リセットメッセージがある場合は最上段に残す
        console.log('🔄 チャットログをクリアします');
        if (resetMessage) {
          setChatLogs([
            {
              type: "AI",
              message: {
                model: resetMessage,
                answer: [],
                url: [],
                asset_data: [],
                streaming_completed: true,
              },
            },
          ]);
        } else {
          setChatLogs([]);
        }
      }
    );

    // ストリーミング完了時：段階的に表示
    // 1. まずテキスト完了状態にする（answersを表示）
    setChatLogs((prev) => {
      const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);

      if (targetIndex === -1) return prev;

      return prev.map((log, index) => {
        if (index === targetIndex) {
          return {
            ...log,
            message: {
              ...newMessage,
              streaming_completed: 'text_completed', // テキスト完了状態
            },
          };
        }
        return log;
      });
    });

    // 2. 短いディレイ後、完全完了状態にする（asset_dataを表示）
    setTimeout(() => {
      setChatLogs((prev) => {
        const targetIndex = prev.findIndex((log) => log.id === thisAiMessageId);

        if (targetIndex === -1) return prev;

        return prev.map((log, index) => {
          if (index === targetIndex) {
            return {
              ...log,
              message: {
                ...newMessage,
                streaming_completed: true, // 完全完了
              },
            };
          }
          return log;
        });
      });
    }, 50); // 50msディレイ（UIの段階的表示のため）

    // AI回答完了後は再度スクロールしない（ユーザーメッセージ追加時に既にスクロール済み）
  }

  // 初期ロード時: open=false ならパネルは非表示（CSS側で transition 管理）
  return (
    <div ref={chatRef} className={clsx(styles.chat, open && styles.isOpen)} style={chatBgStyle} data-otai-chat-container>
      <Header setOpen={setOpen} />
      <Main
        ref={mainRef}
        onScroll={(e) => {
          // 背景画像をメッセージのスクロールに追従させる
          if (chatRef.current) {
            chatRef.current.style.backgroundPositionY = `${-e.currentTarget.scrollTop}px`;
          }
        }}
      >
        {chatLogs.map((chatLog, index) => {
          if (chatLog.type === "AI") {
            // いずれかのAIメッセージがストリーミング中は、他のメッセージの
            // 提案回答ボタンも無効化し、並行ストリーミングの発生を防ぐ
            const isAnyStreaming = chatLogs.some(
              (log) => log.type === "AI" && log.message.streaming_completed === false
            );
            return (
              <MessageAi
                key={index}
                message={chatLog.message}
                onAnswerClick={(answer: string) => handleAnswerClick(answer, index)}
                disabled={isAnyStreaming}
              />
            );
          } else if (chatLog.type === "USER") {
            return <MessageUser key={index} message={chatLog.message} />;
          } else if (chatLog.type === "LOADING") {
            return <MessageLoading key={index} />;
          }
        })}
      </Main>
      <Question onUserMessageAdded={scrollToLastUserMessage} />
      <BottomSheet />
      <Aside />
      <a
        href="https://onetouchsalesai-hp.me/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.poweredBy}
      >
        Powered by OnetouchAI
      </a>
    </div>
  );
}
